import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Autocomplete, Alert,
  CircularProgress, Checkbox, FormControlLabel, Divider, Typography, IconButton,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createInspection, listNonconformityTypes, type CreateInspectionPayload } from '../../api/productieCalitate'
import { listArticles } from '../../api/nomenclatoare'
import { listWarehouses } from '../../api/depozite'
import { listProductionOrders } from '../../api/productieOrdine'
import { listUsers } from '../../api/admin'
import { errorMessage } from '../../api/client'

const ncSchema = z.object({
  nonconformityTypeId: z.string().min(1, 'Selectați tipul'),
  cause: z.string().optional(),
  severity: z.enum(['Minora', 'Majora', 'Critica']),
  correctiveAction: z.string().optional(),
  responsibleUserId: z.string().nullable(),
  dueDate: z.string().optional(),
})
const schema = z.object({
  checkpoint: z.enum(['ControlProces', 'ControlFinal']),
  articleId: z.string().min(1, 'Selectați articolul'),
  lotNumber: z.string().optional(),
  productionOrderId: z.string().nullable(),
  warehouseId: z.string().min(1, 'Selectați depozitul'),
  sampleWeightGrams: z.string().optional(),
  measuredDimensionMm: z.string().optional(),
  wallThicknessMm: z.string().optional(),
  visualDefectsFound: z.boolean(),
  decision: z.enum(['Acceptat', 'Respins', 'Rework', 'Carantina']),
  quantityThousands: z.string().optional(),
  notes: z.string().optional(),
  nonconformities: z.array(ncSchema),
}).refine((v) => v.checkpoint !== 'ControlFinal' || (v.lotNumber?.trim().length ?? 0) > 0, {
  message: 'Controlul final necesită un număr de lot.', path: ['lotNumber'],
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  checkpoint: 'ControlFinal', articleId: '', lotNumber: '', productionOrderId: null, warehouseId: '',
  sampleWeightGrams: '', measuredDimensionMm: '', wallThicknessMm: '', visualDefectsFound: false,
  decision: 'Acceptat', quantityThousands: '', notes: '', nonconformities: [],
}

const SEVERITY_LABEL = { Minora: 'Minoră', Majora: 'Majoră', Critica: 'Critică' }

export default function InspectionFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses, enabled: open })
  const { data: orders } = useQuery({ queryKey: ['productie', 'ordine'], queryFn: listProductionOrders, enabled: open })
  const { data: types } = useQuery({ queryKey: ['productie', 'calitate', 'neconformitati'], queryFn: listNonconformityTypes, enabled: open })
  const { data: users } = useQuery({ queryKey: ['admin', 'users'], queryFn: () => listUsers(), enabled: open })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema), defaultValues: EMPTY,
  })
  const nonconformities = useFieldArray({ control, name: 'nonconformities' })
  const watched = useWatch({ control })
  const checkpoint = watched.checkpoint

  const mutation = useMutation({
    mutationFn: (v: FormValues) => {
      const payload: CreateInspectionPayload = {
        checkpoint: v.checkpoint, articleId: v.articleId, lotNumber: v.lotNumber || null,
        productionOrderId: v.productionOrderId, warehouseId: v.warehouseId, zoneId: null, locationId: null,
        sampleWeightGrams: v.sampleWeightGrams ? Number(v.sampleWeightGrams) : null,
        measuredDimensionMm: v.measuredDimensionMm ? Number(v.measuredDimensionMm) : null,
        wallThicknessMm: v.wallThicknessMm ? Number(v.wallThicknessMm) : null,
        visualDefectsFound: v.visualDefectsFound, decision: v.decision,
        quantityThousands: Number(v.quantityThousands) || 0, notes: v.notes || null,
        nonconformities: v.nonconformities.map((n) => ({
          nonconformityTypeId: n.nonconformityTypeId, cause: n.cause || null, severity: n.severity,
          correctiveAction: n.correctiveAction || null, responsibleUserId: n.responsibleUserId, dueDate: n.dueDate || null,
        })),
      }
      return createInspection(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['productie', 'calitate', 'inspectii'] })
      void qc.invalidateQueries({ queryKey: ['stocuri'] })
      reset(EMPTY)
      onClose()
    },
  })

  const handleClose = () => { mutation.reset(); reset(EMPTY); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>Inspecție de calitate nouă</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="checkpoint" control={control} render={({ field }) => (
                <TextField {...field} select label="Punct de control" sx={{ width: 200 }} error={!!errors.checkpoint} helperText={errors.checkpoint?.message}>
                  <MenuItem value="ControlProces">Control în proces (FR-OQC-001)</MenuItem>
                  <MenuItem value="ControlFinal">Control final pe lot (FR-OQC-002)</MenuItem>
                </TextField>
              )} />
              <Controller name="productionOrderId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={orders?.map((o) => o.id) ?? []}
                  getOptionLabel={(id) => orders?.find((o) => o.id === id)?.number ?? id}
                  value={field.value} onChange={(_, v) => field.onChange(v ?? null)}
                  renderInput={(params) => <TextField {...params} label="Ordin de producție (opțional)" />}
                />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="articleId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
                  getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.articleId} helperText={errors.articleId?.message} />}
                />
              )} />
              <Controller name="warehouseId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ width: 220 }} options={warehouses?.map((w) => w.id) ?? []}
                  getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Depozit" error={!!errors.warehouseId} helperText={errors.warehouseId?.message} />}
                />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="lotNumber" control={control} render={({ field }) => (
                <TextField {...field} label={checkpoint === 'ControlFinal' ? 'Lot (obligatoriu)' : 'Lot (opțional)'}
                  sx={{ flexGrow: 1 }} error={!!errors.lotNumber} helperText={errors.lotNumber?.message} />
              )} />
              <Controller name="quantityThousands" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Cantitate decizie (mii buc)" sx={{ width: 200 }} />
              )} />
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Măsurători (FR-OQC-001)</Typography>
            <Stack direction="row" spacing={2}>
              <Controller name="sampleWeightGrams" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Greutate eșantion (g)" sx={{ width: 190 }} />
              )} />
              <Controller name="measuredDimensionMm" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Dimensiune măsurată (mm)" sx={{ width: 200 }} />
              )} />
              <Controller name="wallThicknessMm" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Grosime perete (mm)" sx={{ width: 190 }} />
              )} />
              <Controller name="visualDefectsFound" control={control} render={({ field }) => (
                <FormControlLabel control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Defecte vizuale constatate" />
              )} />
            </Stack>

            <Divider />
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Controller name="decision" control={control} render={({ field }) => (
                <TextField {...field} select label="Decizie" sx={{ width: 200 }} error={!!errors.decision} helperText={errors.decision?.message}>
                  <MenuItem value="Acceptat">Acceptat</MenuItem>
                  <MenuItem value="Respins">Respins</MenuItem>
                  <MenuItem value="Rework">Rework</MenuItem>
                  <MenuItem value="Carantina">Carantină</MenuItem>
                </TextField>
              )} />
              {checkpoint === 'ControlFinal' && (
                <Alert severity="info" sx={{ flexGrow: 1 }}>
                  {watched.decision === 'Acceptat' && 'Cantitatea trece din "În control" în "Liber" (vandabilă).'}
                  {watched.decision === 'Carantina' && 'Cantitatea rămâne "În control", fără mișcare de stoc.'}
                  {(watched.decision === 'Respins' || watched.decision === 'Rework') && 'Cantitatea trece din "În control" în "Blocat".'}
                </Alert>
              )}
            </Stack>
            <Controller name="notes" control={control} render={({ field }) => (
              <TextField {...field} label="Note" fullWidth multiline minRows={2} />
            )} />

            <Divider />
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2">Neconformități (FR-OQC-004/005)</Typography>
              <Button size="small" startIcon={<Add />}
                onClick={() => nonconformities.append({ nonconformityTypeId: '', cause: '', severity: 'Minora', correctiveAction: '', responsibleUserId: null, dueDate: '' })}>
                Adaugă
              </Button>
            </Stack>
            {nonconformities.fields.map((f, i) => (
              <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Controller name={`nonconformities.${i}.nonconformityTypeId`} control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ flexGrow: 1 }} options={types?.map((t) => t.id) ?? []}
                    getOptionLabel={(id) => { const t = types?.find((x) => x.id === id); return t ? `${t.code} — ${t.description}` : id }}
                    value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                    renderInput={(params) => <TextField {...params} label="Tip neconformitate" error={!!errors.nonconformities?.[i]?.nonconformityTypeId}
                      helperText={errors.nonconformities?.[i]?.nonconformityTypeId?.message} />}
                  />
                )} />
                <Controller name={`nonconformities.${i}.severity`} control={control} render={({ field }) => (
                  <TextField {...field} select label="Severitate" sx={{ width: 130 }} error={!!errors.nonconformities?.[i]?.severity}
                    helperText={errors.nonconformities?.[i]?.severity?.message}>
                    {Object.entries(SEVERITY_LABEL).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                  </TextField>
                )} />
                <Controller name={`nonconformities.${i}.cause`} control={control} render={({ field }) => (
                  <TextField {...field} label="Cauză" sx={{ width: 180 }} />
                )} />
                <Controller name={`nonconformities.${i}.correctiveAction`} control={control} render={({ field }) => (
                  <TextField {...field} label="Acțiune corectivă" sx={{ flexGrow: 1 }} />
                )} />
                <Controller name={`nonconformities.${i}.responsibleUserId`} control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ width: 180 }} options={users?.map((u) => u.id) ?? []}
                    getOptionLabel={(id) => { const u = users?.find((x) => x.id === id); return u ? `${u.firstName} ${u.lastName}` : id }}
                    value={field.value} onChange={(_, v) => field.onChange(v ?? null)}
                    renderInput={(params) => <TextField {...params} label="Responsabil" />}
                  />
                )} />
                <Controller name={`nonconformities.${i}.dueDate`} control={control} render={({ field }) => (
                  <TextField {...field} type="date" label="Termen" sx={{ width: 160 }} slotProps={{ inputLabel: { shrink: true } }} />
                )} />
                <IconButton onClick={() => nonconformities.remove(i)}><DeleteOutlined fontSize="small" /></IconButton>
              </Stack>
            ))}

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose}>Anulează</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
